import { i18n } from "@lingui/core";
import IconSearch16 from "@warp-ds/icons/react/search-16";
import { Button } from "@warp-ds/react";
import { useEffect, useRef, useState } from "react";
import type { RelatedSearch } from "../../types/RelatedSearch";
import { trackSemanticSearchSuggestionClick, trackSemanticSearchSuggestionsView } from "../../tracking/pulse";
import { useSearchResults } from "../results/useSearchResults";
import type { ErrorState } from "../stores/suggestionStore";

export type SuggestionsProps = {
	onClick?: (suggestion: string) => void;
	inline?: boolean; // When true, shows only buttons (for inline in results)
	showHeader?: boolean; // When true, shows title and result count
	suggestions: RelatedSearch[];
	isLoading: boolean;
	error: ErrorState | undefined;
};

export function Suggestions({
	onClick,
	inline = false,
	showHeader = true,
	suggestions,
	isLoading,
	error,
}: SuggestionsProps) {
	const { results, currentQuery } = useSearchResults();
	const [buttonsLoading, setButtonsLoading] = useState(true);
	const lastTrackedKeyRef = useRef<string | null>(null);

	const resultCount = results.length >= 100 ? "99+" : results.length;
	const hasResults = results.length > 0;

	// Set buttons loading to false once we have suggestions
	useEffect(() => {
		if (suggestions.length > 0 && !isLoading) {
			setButtonsLoading(false);
		} else if (isLoading) {
			setButtonsLoading(true);
		}
	}, [suggestions.length, isLoading]);

	useEffect(() => {
		if (isLoading || error || suggestions.length === 0) {
			return;
		}

		const queryKey = currentQuery ?? "";
		const suggestionsKey = suggestions.map((item) => item.query).join("|");
		const trackingKey = `${queryKey}:${suggestionsKey}`;

		if (lastTrackedKeyRef.current === trackingKey) {
			return;
		}

		lastTrackedKeyRef.current = trackingKey;
		trackSemanticSearchSuggestionsView(queryKey, suggestions.length);
	}, [currentQuery, suggestions, isLoading, error]);

	// Prepare buttons: use suggestions if available, otherwise use placeholders
	const buttonCount = 3;
	const displayButtons =
		suggestions.length > 0
			? suggestions
			: Array.from({ length: buttonCount }, () => ({
					query: i18n.t({ id: "explore.suggestions.placeholder", message: "Loading..." }),
					score: 0,
				}));

	const handleButtonClick = (suggestionText: string, index: number) => {
		if (buttonsLoading) return;
		if (suggestions.length > 0) {
			trackSemanticSearchSuggestionClick(currentQuery ?? "", suggestionText, index);
		}
		onClick?.(suggestionText);
	};

	// Inline mode: just render the buttons
	if (inline) {
		return (
			<div className="flex flex-wrap items-start content-start gap-12 self-stretch max-w-3xl mx-auto my-16">
				<div className="flex flex-col">
					<span id="suggestions-title" className="t4">
						{i18n.t({
							id: "explore.suggestions.title.discover.more",
							message: "Discover more",
						})}
					</span>
					<div className="flex flex-wrap items-start content-start gap-12 mt-16">
						{error && !buttonsLoading && (
							<div className="text-body text-red-500">
								{i18n.t({
									id: "explore.suggestions.title.error",
									message: "Unable to load suggestions. Please try again.",
								})}
							</div>
						)}
						{!error &&
							displayButtons.map((suggestion, index) => (
								<Button
									key={`${suggestion.query}-${index}`}
									secondary
									type="button"
									loading={buttonsLoading}
									disabled={buttonsLoading}
									aria-label={
										buttonsLoading
											? i18n.t({
													id: "explore.suggestions.buttons.loading",
													message: "Loading suggestion",
												})
											: suggestion.query
									}
									className="flex items-center gap-8 rounded border-1"
									onClick={() => handleButtonClick(suggestion.query, index)}
								>
									<IconSearch16 />
									<span className="text-body font-normal">{suggestion.query}</span>
								</Button>
							))}
					</div>
				</div>
			</div>
		);
	}

	// Regular mode: full section with header
	// Don't show buttons in regular mode if there are results (they'll show inline in SearchResult)
	const showButtons = !hasResults || !currentQuery;

	return (
		<section role="region" aria-labelledby="suggestions-title">
			{showHeader && (
				<div className="flex flex-col mt-24">
					<span id="suggestions-title" className="t4 mb-8">
						{currentQuery
							? i18n.t({
									id: "explore.suggestions.title",
									message: "Based on your search",
								})
							: i18n.t({
									id: "explore.suggestions.title.discover",
									message: "Try to look for",
								})}
					</span>

					{hasResults && (
						<span className="text-detail" id="number-of-results">
							{resultCount} hits
						</span>
					)}
				</div>
			)}

			{showButtons && (
				<div className="flex flex-wrap items-start content-start gap-12 self-stretch max-w-3xl mx-auto mt-16">
					{error && !buttonsLoading && (
						<div className="text-body text-red-500">
							{i18n.t({
								id: "explore.suggestions.title.error",
								message: "Unable to load suggestions. Please try again.",
							})}
						</div>
					)}
					{!error &&
						displayButtons.map((suggestion, index) => (
							<Button
								key={`${suggestion.query}-${index}`}
								secondary
								type="button"
								loading={buttonsLoading}
								disabled={buttonsLoading}
								aria-label={
									buttonsLoading
										? i18n.t({
												id: "explore.suggestions.buttons.loading",
												message: "Loading suggestion",
											})
										: suggestion.query
								}
								className="flex items-center gap-8 rounded border-1"
								onClick={() => handleButtonClick(suggestion.query, index)}
							>
								<IconSearch16 />
								<span className="text-body font-normal">{suggestion.query}</span>
							</Button>
						))}
				</div>
			)}
		</section>
	);
}
